import { useMemo } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import type { AronPlannerTimeline, AronScheduledTask } from "../types";

/**
 * The planner's time axis: one row per day, each run a mark on a 24-hour track.
 *
 * Built for tasks that fire MANY times a day (every 3 h and denser) — a calendar
 * cell per day would collapse those into one dot, so the position of the mark
 * inside the row carries the hour. Days with nothing scheduled are folded into a
 * single line so a 30-day horizon still fits on one screen.
 *
 * The fire times come from the server (same cron parser that runs the task); this
 * component only places them.
 */

const HOUR_LABELS = [0, 6, 12, 18, 24];
/** Faint 3-hour ticks, stronger 6-hour ones — enough rhythm to read a time off the row. */
const MINOR_TICKS = [3, 9, 15, 21];
const MAJOR_TICKS = [6, 12, 18];
const MINUTES_PER_DAY = 24 * 60;

/** Per-task colours — squares, no radius, matching the brutalist surface. */
export const TASK_COLORS = [
  "#dc1f1f",
  "#1f6fdc",
  "#0f7b46",
  "#a16207",
  "#7b2fbe",
  "#c2410c",
  "#0f766e",
  "#be185d",
];

export function taskColor(index: number): string {
  return TASK_COLORS[index % TASK_COLORS.length];
}

interface Mark {
  taskId: string;
  at: Date;
  minuteOfDay: number;
}

interface Day {
  key: string;
  date: Date;
  marks: Mark[];
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

const WEEKDAY_SHORT = ["ND", "PN", "WT", "ŚR", "CZ", "PT", "SO"];

function dayLabels(date: Date, today: Date): { primary: string; secondary: string } {
  const diff = Math.round(
    (startOfDay(date).getTime() - startOfDay(today).getTime()) / 86_400_000,
  );
  const stamp = `${WEEKDAY_SHORT[date.getDay()]} ${String(date.getDate()).padStart(2, "0")}.${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}`;
  if (diff === 0) return { primary: "DZIŚ", secondary: stamp };
  if (diff === 1) return { primary: "JUTRO", secondary: stamp };
  return { primary: stamp, secondary: "" };
}

function fmtTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Days from today to the horizon, each carrying its marks (empty days included). */
export function buildDays(
  timeline: AronPlannerTimeline | undefined,
  days: number,
  now: Date,
): Day[] {
  const byDay = new Map<string, Mark[]>();
  for (const occ of timeline?.occurrences ?? []) {
    const at = new Date(occ.run_at);
    if (Number.isNaN(at.getTime())) continue;
    const key = dayKey(at);
    const mark: Mark = {
      taskId: occ.task,
      at,
      minuteOfDay: at.getHours() * 60 + at.getMinutes(),
    };
    const bucket = byDay.get(key);
    if (bucket) bucket.push(mark);
    else byDay.set(key, [mark]);
  }
  const out: Day[] = [];
  for (let i = 0; i < days; i += 1) {
    const date = startOfDay(new Date(now.getTime() + i * 86_400_000));
    const key = dayKey(date);
    out.push({ key, date, marks: byDay.get(key) ?? [] });
  }
  return out;
}

/** Fold runs of empty days into one line so a long horizon stays readable. */
type Row = { kind: "day"; day: Day } | { kind: "gap"; count: number; key: string };

export function foldEmptyDays(days: Day[]): Row[] {
  const rows: Row[] = [];
  let gap: Day[] = [];
  const flush = () => {
    if (!gap.length) return;
    // A single quiet day still gets its own track — folding it would hide the rhythm.
    if (gap.length === 1) rows.push({ kind: "day", day: gap[0] });
    else rows.push({ kind: "gap", count: gap.length, key: `gap-${gap[0].key}` });
    gap = [];
  };
  for (const day of days) {
    if (day.marks.length === 0) gap.push(day);
    else {
      flush();
      rows.push({ kind: "day", day });
    }
  }
  flush();
  return rows;
}

interface Props {
  tasks: AronScheduledTask[];
  timeline: AronPlannerTimeline | undefined;
  isLoading: boolean;
  days: number;
  onDaysChange: (days: number) => void;
  selectedTaskId: string | null;
  onSelectTask: (taskId: string | null) => void;
  /** "Now" is injected so the axis can be rendered deterministically in tests. */
  now?: Date;
}

const HORIZONS = [7, 14, 30];

export function AronTaskTimeline({
  tasks,
  timeline,
  isLoading,
  days,
  onDaysChange,
  selectedTaskId,
  onSelectTask,
  now,
}: Props) {
  const reference = useMemo(() => now ?? new Date(), [now]);
  const rows = useMemo(
    () => foldEmptyDays(buildDays(timeline, days, reference)),
    [timeline, days, reference],
  );
  const colorOf = useMemo(() => {
    const map = new Map<string, string>();
    tasks.forEach((t, i) => map.set(t.id, taskColor(i)));
    return map;
  }, [tasks]);
  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    tasks.forEach((t) => map.set(t.id, t.name));
    return map;
  }, [tasks]);

  const nowMinute = reference.getHours() * 60 + reference.getMinutes();
  const truncated = timeline?.meta?.truncated_tasks ?? [];

  return (
    <section className="border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--color-text-primary)]">
            Oś uruchomień
          </span>
          {isLoading && (
            <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />
          )}
          <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
            {timeline?.meta?.count ?? 0} uruchomień
          </span>
        </div>
        <div className="flex items-center gap-1">
          {HORIZONS.map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => onDaysChange(h)}
              className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                days === h
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
              }`}
            >
              {h} dni
            </button>
          ))}
        </div>
      </header>

      {tasks.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--color-border-muted)] px-3 py-2">
          {tasks.map((t, i) => {
            const active = selectedTaskId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelectTask(active ? null : t.id)}
                title={
                  t.next_runs.length
                    ? t.cron_human
                    : "Bez przyszłych uruchomień (wstrzymane lub czeka na akceptację)"
                }
                className={`flex items-center gap-1.5 border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                  active
                    ? "border-[var(--color-text-primary)] text-[var(--color-text-primary)]"
                    : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)]"
                } ${t.next_runs.length ? "" : "opacity-50"}`}
              >
                <span
                  className="inline-block h-2 w-2"
                  style={{
                    backgroundColor: taskColor(i),
                    opacity: t.next_runs.length ? 1 : 0.35,
                  }}
                />
                <span className="max-w-[16ch] truncate">{t.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="px-3 pt-2 pb-3">
        <div className="flex items-center gap-3 pb-1">
          <span className="w-20 shrink-0" />
          <div className="relative h-3 flex-1">
            {HOUR_LABELS.map((h) => (
              <span
                key={h}
                className="absolute -translate-x-1/2 font-mono text-[9px] text-[var(--color-text-muted)]"
                style={{ left: `${(h / 24) * 100}%` }}
              >
                {String(h).padStart(2, "0")}
              </span>
            ))}
          </div>
        </div>

        {rows.map((row) =>
          row.kind === "gap" ? (
            <div key={row.key} className="flex items-center gap-3 py-1">
              <span className="w-20 shrink-0" />
              <div className="flex-1 border-t border-dashed border-[var(--color-border)] pt-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
                {row.count} dni bez uruchomień
              </div>
            </div>
          ) : (
            <TimelineRow
              key={row.day.key}
              day={row.day}
              labels={dayLabels(row.day.date, reference)}
                      isToday={dayKey(row.day.date) === dayKey(reference)}
              isWeekend={[0, 6].includes(row.day.date.getDay())}
              nowMinute={nowMinute}
              colorOf={colorOf}
              nameOf={nameOf}
              selectedTaskId={selectedTaskId}
              onSelectTask={onSelectTask}
            />
          ),
        )}

        {!isLoading && (timeline?.meta?.count ?? 0) === 0 && (
          <p className="py-4 text-center font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
            Brak zaplanowanych uruchomień w tym okresie
          </p>
        )}

        {(truncated.length > 0 || timeline?.meta?.truncated_total) && (
          <p className="mt-2 flex items-start gap-1.5 border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-secondary)]">
            <AlertTriangle size={12} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
            Oś jest skrócona — bardzo częste zadania mają więcej uruchomień, niż mieści się
            w tym widoku. Skróć okres, żeby zobaczyć komplet.
          </p>
        )}
      </div>
    </section>
  );
}

function TimelineRow({
  day,
  labels,
  isToday,
  isWeekend,
  nowMinute,
  colorOf,
  nameOf,
  selectedTaskId,
  onSelectTask,
}: {
  day: Day;
  labels: { primary: string; secondary: string };
  isToday: boolean;
  isWeekend: boolean;
  nowMinute: number;
  colorOf: Map<string, string>;
  nameOf: Map<string, string>;
  selectedTaskId: string | null;
  onSelectTask: (id: string | null) => void;
}) {
  const selectedMarks = selectedTaskId
    ? day.marks.filter((m) => m.taskId === selectedTaskId)
    : [];
  return (
    <div className="py-0.5">
      <div className="flex items-center gap-3">
        <div className="w-20 shrink-0 font-mono text-[10px] uppercase tracking-[0.12em]">
          <span
            className={
              isToday
                ? "font-bold text-[var(--color-primary)]"
                : "text-[var(--color-text-primary)]"
            }
          >
            {labels.primary}
          </span>
          {labels.secondary && (
            <span className="ml-1 text-[var(--color-text-muted)]">{labels.secondary}</span>
          )}
        </div>
        <div
          className={`relative h-7 flex-1 border ${
            isToday ? "border-[var(--color-border-strong)]" : "border-[var(--color-border-muted)]"
          } ${isWeekend ? "bg-[var(--color-bg-elevated)]" : "bg-[var(--color-bg-base)]"}`}
        >
          {MINOR_TICKS.map((h) => (
            <span
              key={`minor-${h}`}
              className="absolute top-0 h-full w-px bg-[var(--color-border)] opacity-40"
              style={{ left: `${(h / 24) * 100}%` }}
            />
          ))}
          {MAJOR_TICKS.map((h) => (
            <span
              key={`major-${h}`}
              className="absolute top-0 h-full w-px bg-[var(--color-border-strong)] opacity-60"
              style={{ left: `${(h / 24) * 100}%` }}
            />
          ))}
          {isToday && (
            <>
              {/* The part of today that is already gone — so "next run" reads at a glance. */}
              <span
                className="absolute top-0 left-0 h-full bg-[var(--color-text-primary)] opacity-[0.07]"
                style={{ width: `${(nowMinute / MINUTES_PER_DAY) * 100}%` }}
              />
              <span
                className="absolute top-0 h-full w-px bg-[var(--color-primary)]"
                style={{ left: `${(nowMinute / MINUTES_PER_DAY) * 100}%` }}
                title="teraz"
              />
            </>
          )}
          {day.marks.map((mark, i) => {
            const dimmed = selectedTaskId !== null && selectedTaskId !== mark.taskId;
            return (
              <button
                key={`${mark.taskId}-${i}`}
                type="button"
                onClick={() => onSelectTask(mark.taskId)}
                title={`${fmtTime(mark.at)} · ${nameOf.get(mark.taskId) ?? "zadanie"}`}
                aria-label={`${fmtTime(mark.at)} ${nameOf.get(mark.taskId) ?? ""}`}
                className="group absolute top-0 flex h-full w-3 -translate-x-1/2 justify-center"
                style={{ left: `${(mark.minuteOfDay / MINUTES_PER_DAY) * 100}%` }}
              >
                <span
                  className="h-full transition-transform group-hover:scale-x-[2]"
                  style={{
                    width: dimmed ? 2 : 3,
                    backgroundColor: colorOf.get(mark.taskId) ?? "#dc1f1f",
                    opacity: dimmed ? 0.25 : 1,
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>
      {selectedMarks.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1 pl-[5.75rem] font-mono text-[9px] text-[var(--color-text-muted)]">
          {selectedMarks.map((m, i) => (
            <span key={i} className="tabular-nums">
              {fmtTime(m.at)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
